import { NodeTypeIcon, NodeStatusBadge } from '../shared'
import type { WorkflowRunNode } from '@/types'
import { cn } from '@/lib/utils'
import { AlertTriangle, Bot, ChevronRight, Repeat } from 'lucide-react'
import { formatDurationMs } from '@/lib/timeFormatters'
import { WorkflowErrorCodes } from '@/lib/workflowErrorCodes'

interface Props {
  node: WorkflowRunNode
  isSelected: boolean
  onClick: () => void
}

export function NodeCard({ node, isSelected, onClick }: Props) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left border rounded-lg p-3 transition-all hover:shadow-sm bg-card border-input',
        isSelected && 'bg-primary text-primary-foreground border-primary',
        node.status === 'Skipped' && 'opacity-60',
        (node.status === 'Running' || node.status === 'RetryPending') && !isSelected && 'animate-pulse',
      )}
      data-testid="node-card"
    >
      <div className="flex items-center gap-2">
        <NodeTypeIcon type={node.taskType} className={cn('h-4 w-4 shrink-0', isSelected ? 'text-primary-foreground' : 'text-muted-foreground')} />
        <span className={cn('text-sm font-medium truncate flex-1', node.status === 'Skipped' && 'line-through')}>{node.name}</span>
        {node.errorCode === WorkflowErrorCodes.TokenExhausted && (
          <AlertTriangle className={cn('h-3.5 w-3.5 shrink-0', isSelected ? 'text-primary-foreground/80' : 'text-warning-foreground')} aria-label="Token budget exceeded" />
        )}
        {node.agent && (
          <span
            title={`Agent: ${node.agent.displayName} @${node.agent.version}`}
            data-testid="node-card-agent-chip"
            className={cn(
              'flex shrink-0 items-center gap-0.5 rounded-full border px-1.5 py-0.5 font-mono text-[9px]',
              isSelected ? 'border-primary-foreground/50 text-primary-foreground/80' : 'border-border text-muted-foreground',
            )}
          >
            <Bot className="h-2.5 w-2.5" aria-hidden="true" />
            <span className="sr-only">Agent {node.agent.displayName}, version </span>@{node.agent.version}
          </span>
        )}
        {node.feedbackEntry && (
          <span
            title="feedbackEntry: gate feedback rewinds the loop to this node"
            className="flex shrink-0 items-center gap-0.5 rounded-full border border-purple-foreground bg-purple px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-purple-foreground"
          >
            <Repeat className="h-2.5 w-2.5" /> entry
          </span>
        )}
        <NodeStatusBadge status={node.status} />
        {node.durationMs != null && (
          <span className={cn('text-xs', isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground')}>{formatDurationMs(node.durationMs)}</span>
        )}
        <ChevronRight className={cn('h-4 w-4 shrink-0', isSelected ? 'text-primary-foreground' : 'text-muted-foreground')} />
      </div>
      {node.promptName && (
        <p
          className={cn('mt-1 text-xs truncate', isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground')}
          data-testid="node-card-prompt-name"
        >
          Prompt: {node.promptName}{node.promptId && !node.promptAvailable ? ' (unavailable)' : ''}
        </p>
      )}
      {node.errorMessage && node.status === 'Failed' && !isSelected && (
        <p className="mt-1 text-xs text-danger-foreground truncate">{node.errorMessage}</p>
      )}
    </button>
  )
}
